import { useState, useRef } from "react";
import api, { API } from "@/lib/api";
import { Upload, FileText, X, Loader2, Sparkles, Check, AlertTriangle, Camera } from "lucide-react";
import { toast } from "sonner";

const authUrl = (fileId) => `${API}/files/${fileId}?auth=${localStorage.getItem("token") || ""}`;
const isImage = (ct) => (ct || "").startsWith("image/");

const humanize = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const FIELD_LABELS = (k) => ({
  licence_number: "licence number", licence_expiry: "licence expiry", home_address: "address",
  cpc_expiry: "CPC expiry", tacho_card_expiry: "tacho card expiry", mot_due: "MOT due", service_due: "service due",
}[k] || k.replace(/_/g, " "));

export function FileUpload({ attachments = [], onChange, testid = "file-upload", accept = "image/*,application/pdf", label = "Upload photos / scans (image or PDF)", extractType = null, smartDetect = false, genericMap = false, onExtract, onCreateDraft }) {
  const [busy, setBusy] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [filing, setFiling] = useState(false);
  const [aiResult, setAiResult] = useState(null); // { fields, labels, confidence, needs_review, uncertain, doc_label }
  const [aiWarning, setAiWarning] = useState(null); // re-photo prompt message
  const [lastFile, setLastFile] = useState(null);
  const [lastUploaded, setLastUploaded] = useState(null);
  const inputRef = useRef(null);

  const fileToRecord = async () => {
    const m = aiResult?.match;
    if (!m || !m.id || filing) return;
    setFiling(true);
    try {
      const res = await api.post("/ai/file-to-record", {
        kind: m.kind, record_id: m.id, updates: m.updates || {},
        attachment: lastUploaded, attach_field: m.attach_field,
      });
      const upd = (res.data?.updated || []).map(FIELD_LABELS).filter(Boolean);
      toast.success(`Filed to ${m.label}${upd.length ? " — updated " + upd.join(", ") : ""}`);
      setAiResult(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not file to record"); }
    finally { setFiling(false); }
  };

  const runSmartExtract = async (file) => {
    if (!file) return;
    setAiBusy(true); setAiResult(null); setAiWarning(null); setLastFile(file);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/ai/smart-extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = res.data || {};
      if (d.detected_type && d.fields && Object.values(d.fields).some((v) => v != null && v !== "")) {
        setAiResult(d);
        if (d.image_quality === "poor") setAiWarning(d.quality_note ? `Hard to read: ${d.quality_note}` : "This scan was hard to read — please double-check the details.");
        toast.success(`AI detected a ${d.doc_label} and read the details`);
      } else {
        setAiWarning((d.error || "The AI couldn't read this document.") + " You can enter the details manually.");
        toast.message("AI couldn't auto-read this document");
      }
    } catch (e) {
      setAiWarning("AI reading failed — enter the details manually.");
      toast.error("AI reading failed");
    } finally { setAiBusy(false); }
  };

  const overrideType = async (docType) => {
    if (!lastFile || !docType) return;
    setAiBusy(true); setAiResult(null);
    try {
      const fd = new FormData();
      fd.append("file", lastFile);
      fd.append("doc_type", docType);
      const res = await api.post("/ai/extract-document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = res.data || {};
      if (d.ok) { setAiResult({ ...d, detected_type: docType, doc_label: d.doc_label }); toast.success("Re-read as " + (d.doc_label || docType)); }
      else {
        setAiResult((prev) => ({ types: d.types || prev?.types || [], detected_type: docType, fields: {},
          doc_label: null, needs_review: false,
          override_error: (d.error || "Couldn't read it as that type.") + " Pick another type, or enter the details manually." }));
        toast.message("Couldn't read it as that type — try another");
      }
    } catch { toast.error("Re-read failed"); } finally { setAiBusy(false); }
  };

  const runExtract = async (file) => {
    if (!extractType || !file) return;
    setAiBusy(true);
    setAiResult(null);
    setAiWarning(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", extractType);
      const res = await api.post("/ai/extract-document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = res.data || {};
      const hasFields = d.ok && d.fields && Object.values(d.fields).some((v) => v != null && v !== "");
      if (hasFields) {
        setAiResult(d);
        if (d.image_quality === "poor") {
          setAiWarning(d.quality_note ? `Hard to read: ${d.quality_note} A clearer, straight-on photo improves accuracy.` : "This photo was hard to read — please double-check the details or try a clearer, straight-on photo.");
        }
        toast.success("AI read your document — review the details below");
      } else {
        setAiWarning((d.quality_note ? `${d.quality_note} ` : "") + "Try a clearer, straight-on photo in good light, or enter the details manually.");
        toast.message("AI couldn't read this document — please try a clearer photo");
      }
    } catch (e) {
      setAiWarning("AI extraction failed. Try a clearer, straight-on photo, or enter the details manually.");
      toast.error("AI extraction failed — enter the details manually");
    } finally {
      setAiBusy(false);
    }
  };

  const cleanFields = () => {
    const clean = {};
    Object.entries(aiResult?.fields || {}).forEach(([k, v]) => { if (v != null && v !== "") clean[k] = v; });
    return clean;
  };

  const applyAi = () => {
    if (!aiResult?.fields || !onExtract) return;
    onExtract(genericMap && aiResult.generic_fields ? aiResult.generic_fields : cleanFields());
    toast.success("Applied to the form — check and edit before saving");
    setAiResult(null);
  };

  const createDraft = async () => {
    if (!aiResult?.fields || !onCreateDraft) return;
    setDrafting(true);
    try {
      await onCreateDraft(cleanFields(), aiResult.uncertain || []);
      setAiResult(null);
    } finally {
      setDrafting(false);
    }
  };

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    const fileArr = Array.from(files);
    try {
      const uploaded = [];
      for (const file of fileArr) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ file_id: res.data.file_id, filename: res.data.filename, content_type: res.data.content_type });
      }
      onChange([...attachments, ...uploaded]);
      toast.success(`${uploaded.length} file${uploaded.length > 1 ? "s" : ""} uploaded`);
      setLastUploaded(uploaded[0] || null);
      if (extractType && fileArr[0]) runExtract(fileArr[0]);
      else if (smartDetect && fileArr[0]) runSmartExtract(fileArr[0]);
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); if (!busy) setDragActive(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (busy) return;
    handleFiles(e.dataTransfer?.files);
  };

  const removeAt = (i) => onChange(attachments.filter((_, idx) => idx !== i));

  return (
    <div>
      <label
        data-testid={`${testid}-drop`}
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`flex items-center justify-center gap-2 border-2 border-dashed rounded-md py-4 px-3 text-sm cursor-pointer transition-colors ${
          dragActive ? "border-slate-900 bg-slate-100 text-slate-700" : "border-slate-300 text-slate-500 hover:border-slate-400 hover:bg-slate-50"
        }`}
      >
        {busy ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
        <span>{busy ? "Uploading…" : dragActive ? "Drop files to upload" : label}</span>
        <input
          ref={inputRef}
          data-testid={`${testid}-input`}
          type="file"
          multiple
          accept={accept}
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          disabled={busy}
        />
      </label>

      {(extractType || smartDetect) && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-blue-700">
          <Sparkles size={12} /> {smartDetect ? "AI will auto-detect the document and fill in the details for you." : "AI will read this document and offer to fill in the details for you."}
        </p>
      )}

      {aiBusy && (
        <div data-testid={`${testid}-busy`} className="mt-3 flex items-center gap-2 text-sm text-slate-600">
          <Loader2 size={16} className="animate-spin" /> AI is reading your document…
        </div>
      )}

      {aiWarning && !aiBusy && (
        <div data-testid={`${testid}-rephoto`} className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3">
          <p className="flex items-start gap-1.5 text-sm font-semibold text-amber-900">
            <Camera size={15} className="mt-0.5 shrink-0" /> Hard to read this document
          </p>
          <p className="mt-1 text-xs text-amber-800">{aiWarning}</p>
          <div className="mt-2 flex gap-2">
            <button type="button" data-testid={`${testid}-retake`} onClick={() => { setAiWarning(null); inputRef.current && inputRef.current.click(); }} className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 transition-colors">
              <Camera size={13} /> Try another photo
            </button>
            <button type="button" data-testid={`${testid}-rephoto-dismiss`} onClick={() => setAiWarning(null)} className="rounded-md border border-amber-300 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-white transition-colors">
              I'll enter it manually
            </button>
          </div>
        </div>
      )}

      {aiResult && (
        <div data-testid={`${testid}-review`} className="mt-3 rounded-md border border-blue-200 bg-blue-50/60 p-3">
          <div className="flex items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 text-sm font-semibold text-blue-900">
              <Sparkles size={14} /> {aiResult.doc_label ? `Detected: ${aiResult.doc_label}` : "AI found these details"} {aiResult.needs_review && <span className="text-[10px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">please double-check</span>}
            </p>
            <button type="button" data-testid={`${testid}-dismiss`} onClick={() => setAiResult(null)} className="text-slate-400 hover:text-slate-700"><X size={14} /></button>
          </div>
          {aiResult.types && aiResult.types.length > 0 && (
            <div className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500" data-testid={`${testid}-override`}>
              <span>Not right?</span>
              <select className="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-xs text-slate-700" value={aiResult.detected_type || ""} onChange={(e) => overrideType(e.target.value)}>
                {aiResult.types.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          )}
          <div className="mt-2 space-y-1">
            {Object.entries(aiResult.fields).filter(([, v]) => v != null && v !== "").map(([k, v]) => {
              const flagged = (aiResult.uncertain || []).includes(k);
              return (
                <div key={k} className="flex items-baseline justify-between gap-3 text-sm" data-testid={`${testid}-field-${k}`}>
                  <span className="text-slate-500">{(aiResult.labels && aiResult.labels[k]) || humanize(k)}</span>
                  <span className="flex items-center gap-1.5 text-right">
                    <span className={`font-medium ${flagged ? "text-amber-800" : "text-slate-900"}`}>{String(v)}</span>
                    {flagged && (
                      <span data-testid={`${testid}-flag-${k}`} title="AI wasn't fully sure — please check this" className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded-full">
                        <AlertTriangle size={10} /> check
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
          {aiResult.override_error && (
            <p data-testid={`${testid}-override-error`} className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">{aiResult.override_error}</p>
          )}
          {aiResult.match && aiResult.match.id && (
            <div className="mt-2 rounded-md bg-emerald-50 border border-emerald-200 p-2.5" data-testid={`${testid}-match`}>
              <p className="text-xs text-emerald-900">
                This looks like <b>{aiResult.match.label}</b>'s {aiResult.doc_label?.toLowerCase()}.
                {Object.keys(aiResult.match.updates || {}).length > 0 && <> Filing it will update: {Object.keys(aiResult.match.updates).map(FIELD_LABELS).join(", ")}.</>}
              </p>
              <button type="button" data-testid={`${testid}-file-to-record`} onClick={fileToRecord} disabled={filing}
                className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 transition-colors disabled:opacity-60">
                <Check size={13} /> {filing ? "Filing…" : `File to ${aiResult.match.kind === "vehicle" ? "vehicle" : "driver"} & update`}
              </button>
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {Object.values(aiResult.fields || {}).some((v) => v != null && v !== "") && (
              <button type="button" data-testid={`${testid}-apply`} onClick={applyAi} className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 transition-colors">
                <Check size={13} /> Fill in the form
              </button>
            )}
            {onCreateDraft && Object.values(aiResult.fields || {}).some((v) => v != null && v !== "") && (
              <button type="button" data-testid={`${testid}-create-draft`} onClick={createDraft} disabled={drafting} className="inline-flex items-center gap-1.5 rounded-md border border-slate-900 px-3 py-1.5 text-xs font-semibold text-slate-900 hover:bg-white transition-colors disabled:opacity-50">
                {drafting ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Save as draft
              </button>
            )}
            <button type="button" data-testid={`${testid}-ignore`} onClick={() => setAiResult(null)} className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-white transition-colors">
              Ignore
            </button>
          </div>
        </div>
      )}

      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid={`${testid}-list`}>
          {attachments.map((a, i) => (
            <div key={a.file_id} className="relative group border border-slate-200 rounded-md overflow-hidden w-20 h-20 bg-slate-50 flex items-center justify-center">
              {isImage(a.content_type) ? (
                <img src={authUrl(a.file_id)} alt={a.filename} className="object-cover w-full h-full" />
              ) : (
                <FileText size={26} className="text-slate-400" />
              )}
              <button
                type="button"
                data-testid={`${testid}-remove-${i}`}
                onClick={() => removeAt(i)}
                className="absolute top-0.5 right-0.5 bg-black/70 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AttachmentThumbs({ attachments = [] }) {
  if (!attachments.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-3" data-testid="attachment-thumbs">
      {attachments.map((a) => (
        <a
          key={a.file_id}
          href={authUrl(a.file_id)}
          target="_blank"
          rel="noopener noreferrer"
          className="block border border-slate-200 rounded-md overflow-hidden w-16 h-16 bg-slate-50 flex items-center justify-center hover:border-slate-400 transition-colors"
          title={a.filename}
        >
          {isImage(a.content_type) ? (
            <img src={authUrl(a.file_id)} alt={a.filename} className="object-cover w-full h-full" />
          ) : (
            <FileText size={22} className="text-slate-400" />
          )}
        </a>
      ))}
    </div>
  );
}
